import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Theme } from "@twilio-paste/core/theme";
import React from "react";
import { CookiesProvider } from "react-cookie";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { analytics } from "./analytics";
import { AnalyticsProvider } from "./analytics/context";
import AppRoutes from "./App";
import { signupContext, websiteContext } from "./context";
import { cookieSetOptions } from "./cookieSetOptions";
import { ExternalNavigationProvider } from "./external-navigate";
import { parseAdditionalIssuers } from "./idp-auth";
import reportWebVitals from "./reportWebVitals";
import "./styles/login.scss";
import { UnifiedAuthProvider } from "./unified-auth";

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);

const queryClient = new QueryClient();

const websiteConfig = {
  basename: import.meta.env.VITE_WEBSITE_URL || "https://sendgrid.com",
  context: websiteContext,
};

const signupConfig = {
  basename: import.meta.env.VITE_SIGNUP_HOST || "https://signup.sendgrid.com",
  context: signupContext,
};

const unifiedAuthProvider = {
  proxyDomainForAuthorize: import.meta.env.VITE_PROXY_DOMAIN_FOR_AUTHORIZE,
  domain: import.meta.env.VITE_DOMAIN,
  clientId: import.meta.env.VITE_CLIENT_ID,
  audience: import.meta.env.VITE_AUDIENCE,
  additionalIssuers: parseAdditionalIssuers(import.meta.env.VITE_IDP_ADDITIONAL_ISSUERS),
  enableUnifiedAccountLinking:
    import.meta.env.VITE_ENABLE_UNIFIED_ACCOUNT_LINKING === "true" || false,
  enableUnifiedAccountSwitching:
    import.meta.env.VITE_ENABLE_UNIFIED_ACCOUNT_SWITCHER === "true" || false,
};

const enabledUnifiedAccountLinking = import.meta.env.VITE_ENABLE_UNIFIED_ACCOUNT_LINKING === "true";
const enabledUnifiedAccountSwitcher =
  import.meta.env.VITE_ENABLE_UNIFIED_ACCOUNT_SWITCHER === "true";
const enabledResellerUnifiedAccountLinking =
  import.meta.env.VITE_ENABLE_RESELLER_UNIFIED_ACCOUNT_LINKING === "true";

const appRoutesProps = {
  enabledUnifiedAccountLinking,
  enabledUnifiedAccountSwitcher,
  enabledResellerUnifiedAccountLinking,
};

root.render(
  <React.StrictMode>
    <Theme.Provider theme="twilio">
      <ExternalNavigationProvider {...signupConfig}>
        <ExternalNavigationProvider {...websiteConfig}>
          <QueryClientProvider client={queryClient}>
            <BrowserRouter>
              <CookiesProvider defaultSetOptions={cookieSetOptions()}>
                <AnalyticsProvider analytics={analytics}>
                  <UnifiedAuthProvider {...unifiedAuthProvider}>
                    <AppRoutes {...appRoutesProps} />
                  </UnifiedAuthProvider>
                </AnalyticsProvider>
              </CookiesProvider>
            </BrowserRouter>
          </QueryClientProvider>
        </ExternalNavigationProvider>
      </ExternalNavigationProvider>
    </Theme.Provider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals(console.log);

analytics.init({ apiHost: import.meta.env.VITE_API_BASE_URL || "https://api.sendgrid.com" });
analytics.page({});
